"use client";

import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Loader2, Sparkles } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { useMealPlanDraft } from "@/app/hooks/useMealPlanDraft";
import { AiCreditLimitBanner } from "@/components/shared/AiCreditLimitBanner";
import { SparklesLoading } from "@/components/shared/SparklesLoading";
import { SuggestedDayList } from "@/components/shared/SuggestedDayList";
import { Button } from "@/components/ui/button";
import { trackMealSuggest } from "@/lib/analytics";
import { fadeSlideUpVariants } from "@/lib/animations";
import type { DraftMeal, MealPlanDraftView } from "@/lib/calendar/meal-plan-draft";
import { localToday } from "@/lib/calendar/week";

import { EmptyDashboard } from "./EmptyDashboard";

/**
 * The empty dashboard's free weekly plan (Jeremy, 2026-10-09): a week of suggestions from the user's recipes, off the
 * calendar until "Add all". Dismiss hides it for the week and brings back the empty state.
 */
export function WeeklyDraft() {
  const t = useTranslations("dashboard");
  const tc = useTranslations("calendar");
  const tCommon = useTranslations("common");
  const locale = useLocale();
  const queryClient = useQueryClient();
  const { data: draft, isLoading } = useMealPlanDraft(true);
  const [busy, setBusy] = useState<"add" | "dismiss" | null>(null);
  const [swappingDate, setSwappingDate] = useState<string | null>(null);
  // A swap came back over the weekly AI limit: swaps stop and the limit banner shows.
  const [limitReached, setLimitReached] = useState(false);

  const today = localToday();
  const queryKey = useMemo(() => ["mealPlanDraft", locale, today], [locale, today]);
  const latest = useCallback(
    () => queryClient.getQueryData<MealPlanDraftView | null>(queryKey) ?? null,
    [queryClient, queryKey]
  );

  /** The draft was added or dismissed elsewhere: say so and show the empty state. */
  const gone = useCallback(() => {
    toast.error(t("mealPlanDraftGone"));
    queryClient.setQueryData(queryKey, null);
  }, [queryClient, queryKey, t]);

  // A swap can take 15 seconds, and the user keeps editing meanwhile. So every edit applies to the draft as it is
  // when the edit lands, and saves go out one at a time with the list as it is when each is sent: a slow answer never
  // writes an older list over a newer one.
  const saveChain = useRef<Promise<unknown>>(Promise.resolve());
  const edit = useCallback(
    (change: (meals: DraftMeal[]) => DraftMeal[]) => {
      const current = latest();
      if (!current) return;
      void queryClient.cancelQueries({ queryKey });
      const next: MealPlanDraftView = { ...current, meals: change(current.meals) };
      queryClient.setQueryData(queryKey, next);
      saveChain.current = saveChain.current.then(async () => {
        const now = latest();
        if (!now) return;
        const res = await fetch("/api/meal-plan-draft", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            weekKey: now.weekKey,
            items: now.meals.map(({ recipeId, date }) => ({ recipeId, date })),
          }),
        }).catch(() => null);
        if (res?.ok) return;
        if (res?.status === 404) return gone();
        // A save that failed must not look saved (review finding): say so and show what the server has. Offline,
        // that reload fails too and the screen keeps the edit, which the next save sends again.
        toast.error(t("draftSaveFailed"), { id: "draft-save-failed" });
        await queryClient.refetchQueries({ queryKey });
      });
    },
    [gone, latest, queryClient, queryKey, t]
  );

  const remove = useCallback(
    (meal: DraftMeal) =>
      edit((meals) => meals.filter((m) => !(m.recipeId === meal.recipeId && m.date === meal.date))),
    [edit]
  );

  const swap = useCallback(
    async (date: string) => {
      const current = latest();
      if (!current) return;
      setSwappingDate(date);
      try {
        const res = await fetch("/api/meal-suggestions/regenerate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            locale,
            dayIso: date,
            todayIso: today,
            // The draft's week runs from today, so meals planned in it count toward the swap.
            weekStartIso: today,
            currentSuggestions: current.meals.map((m) => ({
              recipeId: m.recipeId,
              date: m.date,
              title: m.recipe.title,
            })),
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          // Over the weekly limit: the limit banner shows under the list in place of a toast.
          if (data.error === "aiUsageLimitReached") {
            setLimitReached(true);
            return;
          }
          // The error names a calendar or a common message.
          const key = String(data.error) as never;
          toast.error(
            tc.has(key) ? tc(key) : tCommon.has(key) ? tCommon(key) : tc("mealSuggestFailed")
          );
          return;
        }
        trackMealSuggest("day_regenerated");
        edit((meals) =>
          [...meals.filter((m) => m.date !== date), ...(data.suggestions as DraftMeal[])].sort(
            (a, b) => a.date.localeCompare(b.date)
          )
        );
      } catch {
        toast.error(tc("mealSuggestFailed"));
      } finally {
        setSwappingDate(null);
      }
    },
    [edit, latest, locale, tCommon, tc, today]
  );

  const act = useCallback(
    async (action: "add" | "dismiss") => {
      if (!latest()) return;
      setBusy(action);
      try {
        await saveChain.current;
        const draft = latest();
        if (!draft) return;
        const res = await fetch("/api/meal-plan-draft", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action,
            weekKey: draft.weekKey,
            ...(action === "add" && {
              items: draft.meals.map(({ recipeId, date }) => ({ recipeId, date })),
            }),
          }),
        });
        if (res.status === 404) return gone();
        if (!res.ok) throw new Error(String(res.status));
        if (action === "add") {
          trackMealSuggest("confirmed", { count: draft.meals.length });
          toast.success(tc("suggestSuccess", { count: draft.meals.length }));
          await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
          await queryClient.invalidateQueries({ queryKey: ["calendar"] });
        }
        queryClient.setQueryData(queryKey, null);
      } catch {
        toast.error(action === "add" ? tc("suggestConfirmFailed") : t("draftSaveFailed"));
      } finally {
        setBusy(null);
      }
    },
    [gone, latest, queryClient, queryKey, t, tc]
  );

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-muted-foreground">
        <SparklesLoading isLoading className="size-8 text-primary" />
        <p className="text-sm">{t("draftPlanning")}</p>
      </div>
    );
  }

  if (!draft || draft.meals.length === 0) return <EmptyDashboard />;

  // One block per day; a day can hold more than one meal when the preferences ask for it.
  const byDay = new Map<string, DraftMeal[]>();
  for (const meal of draft.meals) byDay.set(meal.date, [...(byDay.get(meal.date) ?? []), meal]);
  const days = [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([iso, meals]) => ({ iso, meals }));

  return (
    <motion.section
      aria-label={t("draftTitle")}
      variants={fadeSlideUpVariants}
      initial="hidden"
      animate="visible"
      className="mx-auto w-full max-w-md space-y-3 px-3 py-4 md:py-8"
    >
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        <Sparkles className="size-3.5 text-primary" aria-hidden />
        {t("draftTitle")}
      </p>

      {/* The limit banner sits first in every AI spot, on web and iOS (Jeremy, 2026-10-10). */}
      {limitReached && <AiCreditLimitBanner />}

      <SuggestedDayList
        days={days}
        todayIso={today}
        locale={locale}
        swappingIso={swappingDate}
        swapDisabled={limitReached}
        onSwap={swap}
        onRemove={remove}
      />

      <div className="flex flex-col gap-2 @md:flex-row @md:justify-end">
        <Button variant="outline" onClick={() => act("dismiss")} disabled={busy !== null}>
          {busy === "dismiss" && <Loader2 className="size-4 animate-spin" aria-hidden />}
          {t("draftDismiss")}
        </Button>
        <Button onClick={() => act("add")} disabled={busy !== null}>
          {busy === "add" && <Loader2 className="size-4 animate-spin" aria-hidden />}
          {t("draftAddAll")}
        </Button>
      </div>
    </motion.section>
  );
}
